import type React from 'react';
import { useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Typography from 'app/ui/components/atoms/typography';
import Overlay from 'app/ui/components/molecules/popup-dialog/overlay';

const jumpAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const ModalContainer = styled.div<{ $triggerJump: boolean }>`
  background-color: ${({ theme: { colors } }) => colors.veryGray};
  border-radius: ${({ theme: { spacing } }) => spacing(3)};
  padding: ${({ theme: { spacing } }) => spacing(6)};
  width: 90vw;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  
  ${({ $triggerJump }) =>
    $triggerJump &&
    css`
      animation: ${jumpAnimation} 0.15s ease-in-out;
    `}

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        width: 30vw;
      `,
    )}
  `}
`;

const ContentWrapper = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => spacing(6)};
`;

const PrimaryText = styled(Typography)`
  margin-bottom: ${({ theme: { spacing } }) => spacing(2)};
  font-size: ${({ theme }) => theme.typography.small};
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  width: 100%;
`;

interface PopupDialogProps {
  primaryText: string;
  secondaryText?: string;
  primaryButton?: React.ReactNode;
  secondaryButton?: React.ReactNode;
  isOpen: boolean;
}

const PopupDialog: React.FC<PopupDialogProps> = ({
  primaryText,
  secondaryText,
  primaryButton,
  secondaryButton,
  isOpen,
}) => {
  const [triggerJump, setTriggerJump] = useState(false);

  const handleOverlayClick = useCallback(() => {
    setTriggerJump(true);
    setTimeout(() => setTriggerJump(false), 150);
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer
        $triggerJump={triggerJump}
        onClick={(e) => e.stopPropagation()}
        data-testid="popup-dialog-container"
      >
        <ContentWrapper>
          <PrimaryText variant="h5" color="white">
            {primaryText}
          </PrimaryText>
          {secondaryText && (
            <Typography variant="body2" color="white">
              {secondaryText}
            </Typography>
          )}
        </ContentWrapper>

        {(primaryButton || secondaryButton) && (
          <ButtonGroup>
            {secondaryButton}
            {primaryButton}
          </ButtonGroup>
        )}
      </ModalContainer>
    </Overlay>
  );
};

export default PopupDialog;
